import React, { Fragment, FunctionComponent } from 'react';
import Chips from '@modules/ui/shared/components/Chips';
import ContentTable from '@modules/ui/shared/components/ContentTable';
import StandardMarkdownNoMargin from '@modules/ui/markdown/components/StandardMarkdownNoMargin';
import { Typography } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import useObjectReferenceUtils from '../../../context/hooks/useObjectReferenceUtils';
import { OpenCloudAPISchema } from '../../../utils/oasTypes';
import OpenCloudType from '../../sharedUI/components/OpenCloudType';

interface OpenCloudApiTableProps {
  data: [string, NonNullable<OpenCloudAPISchema>][];
  className?: string;
}

const OpenCloudApiTable: FunctionComponent<React.PropsWithChildren<OpenCloudApiTableProps>> = ({
  className,
  data,
}) => {
  const { dereference, isObjectReference } = useObjectReferenceUtils();
  const { translate } = useTranslation();

  return (
    <ContentTable
      className={className}
      data={data}
      renderer={(row) => {
        const [name, schema] = row as [
          string,
          NonNullable<OpenCloudAPISchema & { nullable?: boolean }>,
        ];
        let schemaDescription;
        if (isObjectReference(schema)) {
          const objectReference = dereference(schema);

          if (objectReference === null) {
            throw new Error('Unknown object reference!');
          }

          const { content } = objectReference;
          schemaDescription = content.description;
        } else {
          schemaDescription = schema?.description;
        }

        const tags = schema.required ? [translate('Label.Required')] : [];

        return {
          name: (
            <Fragment>
              <Typography variant='body2'>{`${name} `}</Typography>
              <OpenCloudType schema={schema} />
            </Fragment>
          ),
          tags: tags.length > 0 ? <Chips labels={tags} /> : null,
          summary: schemaDescription && (
            <StandardMarkdownNoMargin>{schemaDescription}</StandardMarkdownNoMargin>
          ),
        };
      }}
    />
  );
};

export default OpenCloudApiTable;
